import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import * as platformTenantSessionsApi from "../api/platformTenantSessions.api";

import { patchStoredAuthSession } from "@/auth/authSessionStorage";
import { normalizeRole, setUser, type DataScopeType, type User } from "@/auth/authSlice";
import { setPermissions } from "@/auth/permissionsSlice";
import {
  enterSupportSession,
  exitSupportSession,
  type EnterSupportSessionPayload,
} from "@/features/platformOwner/platformSessionSlice";
import type { AppDispatch, RootState } from "@/redux/store";

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * Starts a Platform Support Session for a tenant: swaps the active access token for the
 * session's tenant-scoped token, stashes the platform owner's own token/user so Exit can
 * restore it, and dispatches setUser with the target tenant's id — which is what makes
 * TenantBrandingBootstrap/PermissionProvider (both already keyed off user.tenantId) pick up
 * that tenant's real branding/context automatically.
 */
export function useStartPlatformSupportSession() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentPermissionCodes = useSelector(
    (state: RootState) => state.userPermissions.permissionCodes,
  );

  return useMutation({
    mutationFn: (tenantId: string) =>
      platformTenantSessionsApi.startPlatformTenantSession(tenantId),
    onSuccess: (session) => {
      if (!currentUser) return;

      const homeAccessToken = localStorage.getItem("accessToken") ?? "";
      const homeTokenExpiry = localStorage.getItem("tokenExpiry") ?? "";

      // Drop every cached query before the identity swap below. Only `academicYears` is
      // tenant-keyed; classes/sections/students/timetable/etc. are not, so without this the
      // previous tenant's rows would be served to the new one. This has to happen *here*
      // rather than in a provider effect reacting to tenantId: React batches the dispatches
      // below, so this call is guaranteed to land before any component re-renders and
      // subscribes a query for the new tenant. The reverse order (effect-based) cancels the
      // new tenant's in-flight fetches instead — that was the regression.
      // removeQueries(), not clear(): clear() also wipes the MutationCache, and we are
      // currently inside a live mutation's own callback.
      queryClient.removeQueries();

      const supportSession: EnterSupportSessionPayload = {
        sessionId: session.sessionId,
        tenantId: session.tenantId,
        tenantName: session.tenantName,
        expiresAtUtc: session.expiresAtUtc,
        homeAccessToken,
        homeTokenExpiry,
        homeUser: currentUser,
        homePermissionCodes: currentPermissionCodes,
      };

      dispatch(enterSupportSession(supportSession));

      localStorage.setItem("accessToken", session.accessToken);
      localStorage.setItem(
        "tokenExpiry",
        String(Math.floor(Date.parse(session.expiresAtUtc) / 1000)),
      );

      const nextUser: User = {
        ...currentUser,
        tenantId: session.tenantId,
        tenantName: session.tenantName,
        schoolId: session.tenantId,
        isPlatformSupportSession: true,
        platformSupportSessionId: session.sessionId,
        // axios.ts's getCurrentAccessToken() prefers state.auth.user.accessToken over
        // localStorage — this must be kept in sync with the token swap above, or every
        // request (including ending the session) silently keeps using the old token.
        accessToken: session.accessToken,
        refreshToken: undefined,
        // Every field below used to be silently inherited from the Platform Owner's own
        // pre-switch identity (via the spread above), which is what broke this feature: a
        // Platform-Owner-only login has no tenant role, so their own `role` normalizes to
        // "parent" (see normalizeRole's fallback in authSlice.ts), which isGuardianUser()
        // then reads as a Guardian session — rendering the wrong UI entirely. The backend
        // now resolves the tenant's canonical Admin role for this session and returns real
        // identity/permission fields for it, so every one of these must come from `session`,
        // not from whoever the caller happened to be a moment ago.
        role: normalizeRole([session.role]),
        key: session.activeContext.toLowerCase(),
        dataScope: session.dataScope as DataScopeType,
        domainType: session.domainType ?? undefined,
        domainIdentities: [],
        activeContext: session.activeContext,
        activeDomainEntityId: session.activeDomainEntityId ?? undefined,
        scope: "TENANT",
        // Clears the can() Platform-Owner bypass (PermissionProvider.tsx) — this session must
        // be authorized like the tenant's real Admin role, not via a leftover platform claim.
        platformRoles: [],
      };
      dispatch(setUser(nextUser));
      dispatch(setPermissions(session.effectivePermissionKeys));
      // Persist identity, permissions and the support session together in the one envelope
      // initializeAuthAsync reads on bootstrap — that is what makes a hard refresh resume the
      // session (tenant, Admin permissions, banner, Exit) instead of silently dropping half of it.
      patchStoredAuthSession({
        user: nextUser,
        effectivePermissionKeys: session.effectivePermissionKeys,
        permissionVersion: session.permissionVersion,
        // The stored envelope holds the full slice shape, which carries the `active` flag the
        // reducer sets for itself when it handles the payload above.
        platformSession: { ...supportSession, active: true },
      });

      toast.success(`Platform Owner Access: viewing ${session.tenantName}`);
      // Land in the same tenant-facing app shell a normal Admin login lands in
      // (mirrors useLoginController's post-auth navigate), not the Platform
      // Owner's tenant detail page — the support session should feel like a
      // real tenant session, not a continuation of the platform console.
      navigate("/dashboard", { replace: true });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to start support session"));
    },
  });
}

/**
 * Ends the active Platform Support Session. The end-session API call is best-effort — a
 * 401 here (e.g. the session already expired) is expected and doesn't block restoring the
 * platform owner's own session locally.
 */
export function useEndPlatformSupportSession() {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const platformSession = useSelector((state: RootState) => state.platformSession);

  return useMutation({
    mutationFn: () => platformTenantSessionsApi.endPlatformTenantSession(),
    onSettled: () => {
      // Same reasoning as the start path: drop the tenant's cached data before restoring
      // the Platform Owner's identity, so none of it is served back to them as their own.
      queryClient.removeQueries();

      if (platformSession.homeAccessToken) {
        localStorage.setItem("accessToken", platformSession.homeAccessToken);
      }
      if (platformSession.homeTokenExpiry) {
        localStorage.setItem("tokenExpiry", platformSession.homeTokenExpiry);
      }
      if (platformSession.homeUser) {
        dispatch(setUser(platformSession.homeUser));
      }
      // Clear the persisted support session in the same write that restores the owner's
      // identity, so a refresh right after Exit can never resurrect a session that has
      // already been ended server-side. `user` is only included when there is one to write —
      // a key set to undefined is dropped from the envelope, which would erase the identity.
      patchStoredAuthSession({
        ...(platformSession.homeUser ? { user: platformSession.homeUser } : {}),
        effectivePermissionKeys: platformSession.homePermissionCodes ?? [],
        platformSession: undefined,
      });
      // Otherwise the tenant Admin's permission codes linger in Redux after Exit — harmless
      // today only because the Platform Owner's own can() check bypasses permissionCodes
      // entirely via their platformRoles claim, but still a stale-state leak worth closing.
      dispatch(setPermissions(platformSession.homePermissionCodes ?? []));
      dispatch(exitSupportSession());
    },
  });
}
